import React, { useEffect, useState } from "react";
import Highlighter from "react-highlight-words";
import { MdDelete, MdDeleteOutline } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { RiFileExcel2Line } from "react-icons/ri";
import { deleteGroupDelete, deleteRowDelete, updateDefaultFieldsArray } from "./candidateListFunctions";
import SettingsPopup from "../../../../../../ReusableComponents/SettingsPopup/SettingsPopup";
import { IoSettingsOutline } from "react-icons/io5";
import { FaUserEdit } from "react-icons/fa";
import { HiOutlineSquaresPlus } from "react-icons/hi2";
import GroupEditPopup from "./GroupEditPopup";
import { useDispatch } from "react-redux";
import { setAppError } from "../../../../../../store/userSlice";
import PromptElement from "../../../../PromptElement";
import selectionList from "./excel/SelectionList";
import CandidateFilterPopup from "./CandidateFilterPopup";
import { AiOutlineCloudUpload, AiOutlineFilter, AiOutlineUserAdd } from "react-icons/ai";
import EditCandidate from "./EditCandidate";
import GroupEditPopupVisaDetails from "./sections/UpdateVisaDetails/GroupEditPopupVisaDetails";
import EditUpdatedVisaCandidate from "./sections/UpdateVisaDetails/EditUpdatedVisaCandidate";
import EditVisaCancelStatus from "./EditVisaCancelStatus";
import EditVisaCancelReqFullfillStatus from "./EditVisaCancelReqFullfillStatus";
import GroupEditPopupCancelled from "./GroupEditPopupCancelled";
import Toast from "../../../../../../utils/Toast";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import nodeinstance from "../../../../../../nodeInstance";
import SearchFilter from "../../../../../../components/SmartDataTable/SearchFilter";
import PaginationControls from "../../../../../../components/SmartDataTable/PaginationControls";

const candidatesAreNotEditable = ["TRAVELLING", "ARRIVED", "VISA STAMPED"];
const nonDeletableStatus = ["VISA STAMPED", "TRAVELLING", "ARRIVED", "NOT TRAVELLED"];
const sameEditPopupStatus = ["SELECTED", "MEDICALLY UNFIT", "BACKED OUT", "UNDER PROCESS FOR VISA STAMP", "NOT TRAVELLED"];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ListDataCandidate({ candidateData = [], setCandidateListData, projectId, setOPenAddNewPopup, setOpenCandidateUpload }) {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchWords, setSearchWords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [filterView, setFilterView] = useState(false);
  const [filteringKeys, setFilteringKeys] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [openDeletePopup, setOPenDeletePopup] = useState({ open: false, id: "" });
  const [groupEditPopupState, setGroupEditPopupState] = useState({ open: false, status: null });
  const [groupDeletePopupState, setGroupDeletePopupState] = useState({ open: false });
  const [defaultFields, setDefaultFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [editCandidatePopup, setEditCandidatePopup] = useState({ open: false, id: "", editData: "" });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [groupEditCondition, setGroupEditCondition] = useState({ popup: null });
  const [commentDropDownOpen, setCommentDropDownOpen] = useState({ open: false, id: null });
  const [checkedList, setCheckedList] = useState([]);

  const removeTitles = ["visa_profession_name", "visa_stamping_date", "visa_reference_number", "visa_block_number", "candidate_id"];
  const order = ["name", "passport_number", "candidate_id", "job_position_name", "visa_profession_name", "nationality_name", "religion", "status"];
  const options = [{ startingWith: "candidate_file", hide: true }, { startingWith: "candidate_flight", hide: true }];

  useEffect(() => {
    const fields = updateDefaultFieldsArray({ candidateData, removeList: removeTitles, order, options }) || [];
    setDefaultFields(fields);
    setSelectedFields(fields.filter((d) => d.default));
  }, [candidateData]);

  // Filtered data
  const allCandidates = candidateData?.candidates || [];
  const filtered = searchTerm.trim()
    ? allCandidates.filter((item) =>
        selectedFields.some((f) =>
          String(item[f.value] ?? "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : allCandidates;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageData = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setCheckedList([]); }, [safePage, pageSize]);

  const handleSelectAll = () => {
    if (checkedList.length === pageData.length) setCheckedList([]);
    else setCheckedList(pageData.map((d) => d?.id));
  };

  const handleCheckBox = (rowId) => {
    if (checkedList.includes(rowId)) setCheckedList((p) => p.filter((id) => id !== rowId));
    else setCheckedList((p) => [...p, rowId]);
  };

  const deleteData = async (rowID) => {
    const response = await deleteRowDelete(projectId, { candidate: [{ id: rowID }] }, setCandidateListData, dispatch);
    if (response.completed) setOPenDeletePopup({ open: false, id: "" });
  };

  const handleGroupDelete = async () => {
    const arr = [];
    for (let id of checkedList) {
      arr.push({ id });
      const sel = pageData.find((c) => c.id === id);
      if (sel && nonDeletableStatus.includes(sel.status)) {
        dispatch(setAppError({ msg: "Some selected candidates can't be deleted" }));
        return;
      }
    }
    const response = await deleteGroupDelete(projectId, { candidate: arr }, setCandidateListData, setDeleteLoading, dispatch);
    if (response.completed) setGroupDeletePopupState({ open: false });
  };

  const getData = async () => {
    setDownloadLoading(true);
    const NodeToken = localStorage.getItem("nodeToken");
    try {
      const response = await nodeinstance.get(`/dashboard/projects/get-selection-list-template`, { responseType: "blob", headers: { Authorization: `Bearer ${NodeToken}` } });
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Template - Selected List`);
        document.body.appendChild(link);
        link.click();
        dispatch(setAppError({ msg: "Excel Downloaded", color: "success" }));
      } else throw new Error("Something went wrong!");
    } catch (err) {
      dispatch(setAppError({ msg: err.message }));
    } finally { setDownloadLoading(false); }
  };

  // Dummy FilterColumn for CandidateFilterPopup compatibility
  const FilterColumn = () => null;
  const ResetFilter = () => { setFilteringKeys({}); setSearchTerm(""); };

  return (
    <>
      {/* Edit popups */}
      {editCandidatePopup?.open && sameEditPopupStatus.includes(editCandidatePopup.editData.status) ? (
        <EditCandidate showPromptElement={editCandidatePopup} setShowPromptElement={setEditCandidatePopup} candidateListData={candidateData} projectId={projectId} setCandidateListData={setCandidateListData} ResetFilter={ResetFilter} />
      ) : editCandidatePopup?.open && editCandidatePopup.editData.status === "VISA STAMPED" ? (
        <EditUpdatedVisaCandidate showPromptElement={editCandidatePopup} setShowPromptElement={setEditCandidatePopup} candidateListData={candidateData} projectId={projectId} setCandidateListData={setCandidateListData} updateVisaDetails={true} ResetFilter={ResetFilter} />
      ) : editCandidatePopup?.open && editCandidatePopup.editData.status === "VISA CANCELLED" ? (
        <EditVisaCancelStatus showPromptElement={editCandidatePopup} setShowPromptElement={setEditCandidatePopup} candidateListData={candidateData} projectId={projectId} setCandidateListData={setCandidateListData} updateVisaDetails={true} ResetFilter={ResetFilter} />
      ) : editCandidatePopup?.open && editCandidatePopup.editData.status === "VISA CANCELLED REQUIREMENT FULFILLED" ? (
        <EditVisaCancelReqFullfillStatus showPromptElement={editCandidatePopup} setShowPromptElement={setEditCandidatePopup} candidateListData={candidateData} projectId={projectId} setCandidateListData={setCandidateListData} updateVisaDetails={true} ResetFilter={ResetFilter} />
      ) : null}

      {showSettings && <SettingsPopup showPopup={showSettings} setShowPopup={setShowSettings} initialState={defaultFields} finalState={selectedFields} setFinalState={setSelectedFields} />}
      {openDeletePopup.open && <PromptElement showPromptElement={openDeletePopup} setShowPromptElement={setOPenDeletePopup} deleteHandler={deleteData} />}
      {groupDeletePopupState.open && <PromptElement showPromptElement={groupDeletePopupState} setShowPromptElement={setGroupDeletePopupState} deleteHandler={handleGroupDelete} />}

      {groupEditPopupState.open && groupEditCondition.popup === "VISA_Stamp_Popup" ? (
        <GroupEditPopupVisaDetails popupState={groupEditPopupState} setPopupState={setGroupEditPopupState} candidateData={candidateData} setCandidateListData={setCandidateListData} checkedList={checkedList} ResetFilter={ResetFilter} />
      ) : groupEditCondition.popup === "VISA_Cancelled_Popup" ? (
        <GroupEditPopupCancelled popupState={groupEditPopupState} setPopupState={setGroupEditPopupState} candidateData={candidateData} setCandidateListData={setCandidateListData} checkedList={checkedList} ResetFilter={ResetFilter} status={groupEditCondition?.status} />
      ) : groupEditCondition.popup === "Same_Edit_Popup" ? (
        <GroupEditPopup popupState={groupEditPopupState} setPopupState={setGroupEditPopupState} candidateData={candidateData} setCandidateListData={setCandidateListData} checkedList={checkedList} ResetFilter={ResetFilter} />
      ) : groupEditCondition.error ? (
        <div className="fixed max-w-sm w-full top-3 left-1/2 transform -translate-x-1/2 sm:right-5 sm:left-auto sm:transform-none">
          <Toast msg={groupEditCondition?.error} />
        </div>
      ) : null}

      {filterView && (
        <CandidateFilterPopup filterView={filterView} setFilterView={setFilterView} candidateData={candidateData} setCandidateListData={setCandidateListData} FilterColumn={FilterColumn} ResetFilter={ResetFilter} filteringKeys={filteringKeys} statusFilter />
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center w-full mb-4 flex-row-reverse">
        <SearchFilter searchTerm={searchTerm} onSearchChange={(val) => { setSearchTerm(val); setSearchWords(val ? val.trim().split(/\s+/) : []); setCurrentPage(1); }} placeholder="Search across all columns..." />
        <div className="flex flex-wrap gap-2 items-center">
          {/* Download Excel */}
          <div className="relative">
            <div className="peer">
              <button className="flex items-center gap-2 bg-blue text-white p-2 px-3 rounded-md text-sm">
                Download Excel <RiFileExcel2Line size="1.1rem" />
              </button>
            </div>
            <div className="hidden absolute right-0 top-full w-[14rem] bg-white shadow-xl rounded-lg pt-2 px-2 pb-2 ring-1 ring-slate-400 peer-hover:flex hover:flex flex-col gap-2 z-50">
              <button onClick={() => selectionList(candidateData, pageData, "selectionList", checkedList)} disabled={pageData.length <= 0}
                className="flex justify-center items-center gap-2 ring-1 ring-slate-300 font-semibold text-black bg-slate-50 p-2 px-3 rounded-md w-full text-xs disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-blue hover:text-white">
                Selected List Report
              </button>
              <button onClick={() => getData()}
                className="flex justify-center items-center gap-2 ring-1 ring-slate-300 font-semibold text-black bg-slate-50 p-2 px-3 rounded-md w-full text-xs hover:bg-blue hover:text-white">
                Template - Selected List
              </button>
            </div>
          </div>
          <button onClick={() => setOpenCandidateUpload(true)} className="flex items-center gap-2 p-2 px-3 rounded-md bg-blue text-white text-sm">
            Upload Selection List <AiOutlineCloudUpload size="1.1rem" />
          </button>
          <button onClick={() => setOPenAddNewPopup(true)} className="flex items-center gap-2 p-2 px-3 rounded-md bg-blue text-white text-sm">
            Add New Candidate <AiOutlineUserAdd size="1.1rem" />
          </button>
          {/* Group Action */}
          <div className="relative">
            <div className="peer">
              <button className="flex items-center gap-2 bg-blue text-white p-2 px-3 rounded-md text-sm">
                Group Action <HiOutlineSquaresPlus size="1.1rem" />
              </button>
            </div>
            <div className="hidden absolute right-0 top-full w-[12rem] bg-white shadow-xl rounded-lg pt-2 px-2 pb-2 ring-1 ring-slate-400 peer-hover:flex hover:flex flex-col gap-1 z-50">
              <button
                onClick={() => {
                  const checked = pageData?.filter((d) => checkedList.includes(d?.id));
                  if (checked.find((d) => candidatesAreNotEditable.includes(d?.status))) {
                    dispatch(setAppError({ msg: "Some candidates are not allowed to edit." }));
                  } else {
                    setGroupEditCondition(checkCandidateStatus(pageData, checkedList));
                    setGroupEditPopupState({ open: true });
                  }
                }}
                disabled={checkedList.length === 0}
                className="flex justify-center items-center gap-2 bg-blue text-white p-2 px-3 rounded-md w-full text-xs disabled:bg-blue/70 disabled:cursor-not-allowed"
              >
                Group Edit <FaUserEdit size="1.1rem" />
              </button>
              <button onClick={() => setGroupDeletePopupState({ open: true })} disabled={checkedList.length === 0}
                className="flex justify-center items-center gap-2 bg-red text-white p-2 px-3 rounded-md w-full text-xs disabled:bg-red/70 disabled:cursor-not-allowed">
                Group Delete <MdDelete size="1.1rem" />
              </button>
            </div>
          </div>
          <button className="flex items-center gap-2 bg-blue text-white p-2 px-3 rounded-md text-sm disabled:bg-blue/70 disabled:cursor-not-allowed" onClick={() => setFilterView(true)} disabled={pageData.length <= 0}>
            Filter <AiOutlineFilter size="1.1rem" />
          </button>
          <button onClick={() => setShowSettings(true)} className="disabled:cursor-not-allowed" disabled={pageData.length <= 0}>
            <IoSettingsOutline size="1.2rem" />
          </button>
        </div>
      </div>

      <p className="text-sm w-full text-right font-medium mb-1 xl:hidden block">{`Slide / scroll to right >`}</p>

      {/* Table */}
      <div className="p-2 overflow-x-auto bg-white rounded-lg shadow max-h-[70vh]">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm" style={{ backgroundColor: '#f9fafb' }}>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
                <div className="flex items-center gap-2 cursor-pointer" onClick={handleSelectAll}>
                  <input type="checkbox" readOnly checked={checkedList.length === pageData.length && pageData.length > 0} className="checkbox checkbox-sm" />
                </div>
              </th>
              <th className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50 whitespace-nowrap" style={{ backgroundColor: '#f9fafb' }}>S.NO.</th>
              {selectedFields.map((f) => (
                <th key={f.id} className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50 whitespace-nowrap" style={{ backgroundColor: '#f9fafb' }}>{f.title}</th>
              ))}
              <th className="px-6 py-4 text-left text-base font-semibold text-gray-700 uppercase tracking-wider bg-gray-50 whitespace-nowrap" style={{ backgroundColor: '#f9fafb' }}>Action</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {allCandidates.length === 0 ? (
              <tr><td colSpan={selectedFields.length + 3} className="px-6 py-8 text-center text-gray-500">No candidates found.</td></tr>
            ) : (
              pageData.map((data, index) => (
                <React.Fragment key={data?.id}>
                  <tr className="border-b-[1.5px] border-slate-100 hover:bg-gray-50">
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={checkedList.includes(data?.id)} onChange={() => handleCheckBox(data?.id)} className="checkbox checkbox-sm" />
                        {data?.visa_cancelled_comments && (
                          commentDropDownOpen?.open && commentDropDownOpen?.id === data?.id
                            ? <IoIosArrowUp className="cursor-pointer" onClick={() => setCommentDropDownOpen({ open: false })} />
                            : <IoIosArrowDown className="cursor-pointer" onClick={() => setCommentDropDownOpen({ open: true, id: data?.id })} />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-nowrap text-base text-gray-700">{(safePage - 1) * pageSize + index + 1}</td>
                    {selectedFields.map((f) => (
                      <td key={f.id} className="px-6 py-6 whitespace-nowrap text-base text-gray-700">
                        <Highlighter searchWords={searchWords} autoEscape textToHighlight={data[f.value] !== undefined && data[f.value] !== null ? String(data[f.value]) : "-"} />
                      </td>
                    ))}
                    <td className="px-6 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button className="p-1 px-2 rounded-md ring-1 ring-slate-600 disabled:bg-slate-200 disabled:cursor-not-allowed" disabled={candidatesAreNotEditable.includes(data?.status)} onClick={() => setEditCandidatePopup({ open: true, id: data?.id, editData: data })}>
                          <CiEdit size="1.4rem" />
                        </button>
                        <button className="p-1 px-2 rounded-md ring-1 ring-red text-white bg-red disabled:bg-red/60 disabled:cursor-not-allowed" onClick={() => setOPenDeletePopup({ open: true, id: data.id })} disabled={nonDeletableStatus.includes(data.status)}>
                          <MdDeleteOutline size="1.4rem" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {data?.visa_cancelled_comments && commentDropDownOpen.open && commentDropDownOpen?.id === data?.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={selectedFields.length + 3} className="px-8 py-2 text-sm text-gray-600">
                        Comment: {data?.visa_cancelled_comments}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls currentPage={safePage} totalPages={totalPages} pageSize={pageSize} totalRecords={filtered.length} pageSizeOptions={PAGE_SIZE_OPTIONS} onPageChange={(p) => setCurrentPage(p)} onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }} />
    </>
  );
}

const checkCandidateStatus = (candidateData = [], checkedList = []) => {
  const checked = candidateData.filter((d) => checkedList.includes(d?.id));
  if (checked.every((d) => sameEditPopupStatus.includes(d?.status))) return { popup: "Same_Edit_Popup" };
  if (checked.every((d) => d?.status === "VISA STAMPED")) return { popup: "VISA_Stamp_Popup" };
  if (checked.every((d) => d?.status === "VISA CANCELLED")) return { popup: "VISA_Cancelled_Popup", status: "VISA CANCELLED" };
  if (checked.every((d) => d?.status === "VISA CANCELLED REQUIREMENT FULFILLED")) return { popup: "VISA_Cancelled_Popup", status: "VISA CANCELLED REQUIREMENT FULFILLED" };
  return { popup: null, error: "You are not allowed to perform group edit for these candidates." };
};
